<template>
  <div id="listWidget" class="mb-5">
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-info-circle me-2 text-primary"></i>
        <span class="instruction-title">사용 안내</span>
      </div>
      <div class="instruction-body">
        <p class="mb-0" style="font-size: 0.85rem; line-height: 1.6; color: #495057;">
          연결할 조명을 <code style="font-size: 100%">포함</code>/<code style="font-size: 100%">미포함</code>으로
          선택한 뒤 <code style="font-size: 100%">등록</code>/<code style="font-size: 100%">수정</code>을 눌러주세요.
          (이 화면의 포함/미포함은 현재 방 점등과 무관하며, 버튼에 매핑할 조명만 선택합니다.
          밝기·색온도는 방 화면에서 설정하세요.
          포함된 조명은 상태 표시를 켜면, 이 버튼을 켰을 때 앱에서도 켜짐으로 보입니다.
          포함을 해제하면 상태 표시도 함께 해제됩니다.)
          다른 방 조명도 아래에서 방 이름을 눌러 펼친 뒤 선택할 수 있습니다.
        </p>
        <p class="mb-0 mt-2" style="font-size: 0.8rem; line-height: 1.5; color: #6c757d;">
          같은 제어기 채널은 네트워크 없이도 물리 버튼으로 함께 켜지고 꺼집니다.
          다른 제어기 조명은 인터넷 연결이 필요합니다.
        </p>
      </div>
    </div>

    <div class="name-edit-card card-item mb-3" v-if="allSelectableLights.length > 0">
      <div class="name-edit-body d-flex align-items-center">
        <div class="menu-search-input flex-fill" style="padding-right: 10px">
          <select class="form-control" v-model="deviceName" :disabled="isEditMode">
            <option value="" style="display: none">장치이름을 선택해주세요.</option>
            <option
              v-for="index in 99"
              :key="index"
              :value="`${index}번`"
              :disabled="isExistIndex(index)"
            >{{ index }}번</option>
          </select>
        </div>
        <button class="btn btn-primary" @click="onSaveClick">{{ isEditMode ? '수정' : '등록' }}</button>
      </div>
      <div v-if="!isEditMode && portQuery != null" class="px-3 pb-2 text-muted" style="font-size: 0.8rem;">
        연결 포트: {{ toDisplayLampPort(portQuery, serialQuery) }} · {{ serialQuery }}
      </div>
    </div>

    <div class="section-card card-item mb-3" v-if="availableLights.length > 0">
      <div class="section-header">
        <i class="fa-regular fa-lightbulb me-2 text-primary"></i>
        <span class="section-title">조명 선택 ( {{ group?.name }} )</span>
      </div>
      <div class="section-body">
        <div class="row">
          <LightMappingDeviceItem
            v-for="item in availableLights"
            :key="item._id"
            :group="group"
            :device="item"
            :selectedDevices="selectedLights"
            @deviceChecked="handleDeviceChecked"
            @showStateChanged="handleShowStateChanged"
          />
        </div>
      </div>
    </div>

    <div
      v-for="og in otherRoomGroups"
      :key="og._id"
      class="section-card card-item mb-3"
    >
      <div
        class="section-header section-header-toggle"
        :class="{ 'section-header-collapsed': !isOtherRoomExpanded(og._id) }"
        role="button"
        @click="toggleOtherRoom(og._id)"
      >
        <i class="fa-brands fa-microsoft me-2 text-primary"></i>
        <span class="section-title flex-fill">{{ og.name }}</span>
        <span
          v-if="selectedCountInGroup(og) > 0"
          class="other-room-badge me-2"
        >포함 {{ selectedCountInGroup(og) }}</span>
        <i
          :class="`fa-solid ${isOtherRoomExpanded(og._id) ? 'fa-chevron-up' : 'fa-chevron-down'} text-muted`"
          style="font-size: 0.85rem;"
        ></i>
      </div>
      <div v-if="isOtherRoomExpanded(og._id)" class="section-body">
        <div class="row">
          <LightMappingDeviceItem
            v-for="item in physicalLampsOf(og)"
            :key="item._id"
            :group="og"
            :device="item"
            :selectedDevices="selectedLights"
            @deviceChecked="handleDeviceChecked"
            @showStateChanged="handleShowStateChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  getCurrentInstance,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  nextTick,
} from 'vue';
import Axios, { GroupApi, VirtualDeviceApi } from '@/api';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'vuex';
import LightMappingDeviceItem from './components/LightMappingDeviceItem.vue';
import useMqttControl from '@/common/hooks/useMqttControl';
import { buildButtonMapsForController, publishButtonMapsPayload } from '@/common/utils/buttonMaps';
import { toDisplayLampPort } from '@/common/utils/controllerDisplayName';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const route = useRoute();
const store = useStore();

const deviceName = ref('');
const srcDevices = ref([]);
const selectedLights = ref([]);
const mappedItems = ref([]);
const expandedOtherRoomIds = ref([]);

const zones = computed(() => store.getters['home/getZones']);

const groups = computed(() => {
  const list = [];
  zones.value?.forEach((zone) => list.push(...(zone.groups || [])));
  return list;
});

const group = computed(() => groups.value?.find((g) => String(g._id) === String(route.params.groupId)));

const buttonDevice = computed(() => {
  return (group.value?.devices || []).find((d) => String(d._id) === String(route.params.deviceId));
});

const isEditMode = computed(() => !!route.params.deviceId);
const portQuery = computed(() => route.query.port);
const serialQuery = computed(() => route.query.serial);

const physicalLampsOf = (g) => {
  const excludeId = route.params.deviceId;
  return (g?.devices || []).filter(
    (d) => d.type === 1 && !d.isVirtual && String(d._id) !== String(excludeId),
  );
};

const currentZone = computed(() => {
  const gid = String(route.params.groupId);
  return zones.value?.find((z) => (z.groups || []).some((g) => String(g._id) === gid));
});

const availableLights = computed(() => physicalLampsOf(group.value));

const otherRoomGroups = computed(() => {
  const gid = String(route.params.groupId);
  return (currentZone.value?.groups || []).filter(
    (g) => String(g._id) !== gid && physicalLampsOf(g).length > 0,
  );
});

const allSelectableLights = computed(() => {
  const list = [...availableLights.value];
  for (const og of otherRoomGroups.value) {
    list.push(...physicalLampsOf(og));
  }
  return list;
});

const findGroupIdForDevice = (deviceId) => {
  const sid = String(deviceId);
  if (availableLights.value.some((d) => String(d._id) === sid)) {
    return group.value?._id;
  }
  for (const og of otherRoomGroups.value) {
    if (physicalLampsOf(og).some((d) => String(d._id) === sid)) {
      return og._id;
    }
  }
  return group.value?._id;
};

const isOtherRoomExpanded = (groupId) => expandedOtherRoomIds.value.includes(String(groupId));

const toggleOtherRoom = (groupId) => {
  const sid = String(groupId);
  if (expandedOtherRoomIds.value.includes(sid)) {
    expandedOtherRoomIds.value = expandedOtherRoomIds.value.filter((id) => id !== sid);
  } else {
    expandedOtherRoomIds.value = [...expandedOtherRoomIds.value, sid];
  }
};

const selectedCountInGroup = (og) => {
  const ids = new Set(physicalLampsOf(og).map((d) => String(d._id)));
  return selectedLights.value.filter((d) => ids.has(String(d._id))).length;
};

const expandRoomsForSelected = () => {
  const currentGid = String(route.params.groupId);
  const ids = new Set();
  for (const sel of selectedLights.value) {
    const gid = findGroupIdForDevice(sel._id);
    if (gid != null && String(gid) !== currentGid) {
      ids.add(String(gid));
    }
  }
  expandedOtherRoomIds.value = [...ids];
};

const existIndices = computed(() => {
  const regex = /[^0-9]/g;
  return (group.value?.devices || [])
    .filter((item) => item.name && item.type === 1 && !item.isVirtual)
    .map((item) => parseInt(item.name.replace(regex, ''), 10))
    .filter((n) => !Number.isNaN(n));
});

const isExistIndex = (index) => {
  if (isEditMode.value && deviceName.value === `${index}번`) return false;
  return existIndices.value.includes(index);
};

const toScalar = (raw, fallback = 0) => {
  if (Array.isArray(raw)) {
    return toScalar(raw[0], fallback);
  }
  if (raw === undefined || raw === null || raw === '') {
    return fallback;
  }
  const n = Number(raw);
  return Number.isNaN(n) ? fallback : n;
};

const resolveLampItemState = (device) => {
  const item = device?.item ?? {};
  if (device?.onVirtual === true) {
    const st = item.state ?? {};
    return {
      onoff: toScalar(st.onoff ?? 0, 0),
      bright: toScalar(st.bright ?? item.bright, 50),
      color: toScalar(st.color ?? item.color, 0),
    };
  }
  return {
    onoff: toScalar(item.onoff, 0),
    bright: toScalar(item.bright, 50),
    color: toScalar(item.color, 0),
  };
};

const publishStatus = async (devices) => {
  const lampItems = (devices || []).filter((d) => d.type === 1);
  const byController = {};
  for (const it of lampItems) {
    const cid = it.controllerId;
    if (!byController[cid]) byController[cid] = [];
    byController[cid].push(it);
  }
  for (const controllerId of Object.keys(byController)) {
    const ctrlGroup = byController[controllerId];
    const portsArr = [];
    const onoffArr = [];
    const brightArr = [];
    const colorArr = [];
    const deviceIds = [];
    for (const it of ctrlGroup) {
      deviceIds.push(it._id);
      const ports = Array.isArray(it.item?.ports) ? it.item.ports : (it.item?.ports != null ? [it.item.ports] : []);
      const lampState = resolveLampItemState(it);
      for (const p of ports) {
        portsArr.push(p);
        onoffArr.push(lampState.onoff);
        brightArr.push(lampState.bright);
        colorArr.push(lampState.color);
      }
    }
    if (portsArr.length > 0) {
      mqttControl.onPublishBatch(controllerId, deviceIds, {
        serial: controllerId,
        cmd: 'cs',
        type: 1,
        data: { ports: portsArr, onoff: onoffArr, bright: brightArr, color: colorArr },
      });
    }
  }
};

const mergeMappedStates = (mapped) => {
  const mapById = {};
  (mapped || []).forEach((item) => {
    mapById[String(item._id)] = item;
  });
  return allSelectableLights.value
    .filter((d) => mapById[String(d._id)])
    .map((d) => {
      const mappedItem = mapById[String(d._id)];
      const mappedState = mappedItem.item?.state ?? {};
      const mergedItem = {
        ...d.item,
        ...mappedItem.item,
        // 매핑 편집 UI: 매핑된 조명은 항상 '켜짐(포함)'으로 표시/미리보기
        onoff: 1,
        bright: toScalar(mappedItem.item?.bright ?? mappedState.bright ?? d.item?.bright, 50),
        color: toScalar(mappedItem.item?.color ?? mappedState.color ?? d.item?.color, 0),
      };
      delete mergedItem.state;
      return { ...d, showState: mappedItem.showState === true, item: mergedItem };
    });
};

/** 매핑 편집 미리보기: 매핑된 조명 ON, 나머지 OFF (방 점등 상태와 분리) */
const buildMappingPreviewDevices = (mappedSelected) => {
  const mappedIds = new Set((mappedSelected || []).map((d) => String(d._id)));
  const mappedById = new Map((mappedSelected || []).map((d) => [String(d._id), d]));
  return allSelectableLights.value.map((d) => {
    const id = String(d._id);
    if (mappedIds.has(id)) {
      const sel = mappedById.get(id);
      return {
        ...d,
        item: {
          ...d.item,
          onoff: 1,
          bright: toScalar(sel?.item?.bright ?? d.item?.bright, 50),
          color: toScalar(sel?.item?.color ?? d.item?.color, 0),
        },
      };
    }
    return {
      ...d,
      item: {
        ...d.item,
        onoff: 0,
        bright: toScalar(d.item?.bright, 50),
        color: toScalar(d.item?.color, 0),
      },
    };
  });
};

const applyOptimisticLampStates = (devices) => {
  for (const d of devices || []) {
    if (d?.type !== 1 || !d?._id) continue;
    store.commit('home/updateDevice', {
      groupId: findGroupIdForDevice(d._id),
      id: d._id,
      data: {
        item: {
          onoff: toScalar(d.item?.onoff, 0),
          bright: toScalar(d.item?.bright, 50),
          color: toScalar(d.item?.color, 0),
        },
      },
    });
  }
};

onBeforeMount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: isEditMode.value ? '조명 버튼 수정' : '조명 버튼 등록',
    isShowMenu: false,
    isShowZone: false,
    isShowProfile: false,
    isShowRightHome: true,
  });
});

onBeforeUnmount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: '',
    isShowMenu: true,
    isShowZone: true,
    isShowProfile: true,
    isShowRightHome: false,
  });
});

onMounted(async () => {
  await store.dispatch('home/fetchAllZones');
  if (isEditMode.value && buttonDevice.value?.name) {
    deviceName.value = buttonDevice.value.name;
  }
  srcDevices.value = JSON.parse(JSON.stringify(allSelectableLights.value));

  if (isEditMode.value) {
    await loadExistingMapping();
  }
});

const loadExistingMapping = async () => {
  try {
    const result = await Axios.get(
      VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', route.params.deviceId),
    );
    if (result.resultCode === '9001') {
      mappedItems.value = [];
      selectedLights.value = [];
      expandedOtherRoomIds.value = [];
      return;
    }
    if (result.resultCode !== '0000') {
      throw new Error(result.resultMessage);
    }
    const items = result.resultData?.items;
    mappedItems.value = Array.isArray(items) ? items : Object.values(items || {});
    selectedLights.value = mergeMappedStates(mappedItems.value);
    expandRoomsForSelected();
    await nextTick();
    // 매핑등 ON / 비매핑 OFF 로 미리보기 (실시간 전체 점등 상태와 분리)
    const previewDevices = buildMappingPreviewDevices(selectedLights.value);
    applyOptimisticLampStates(previewDevices);
    await publishStatus(previewDevices);
  } catch (e) {
    emitter.emit('showPopAlert', {
      description: '버튼 매핑 정보 조회에 실패했습니다.',
      okCallback: () => router.go(-1),
    });
  }
};

onBeforeRouteLeave(async (to, from, next) => {
  try {
    await publishStatus(srcDevices.value);
  } catch (error) {
    console.error('원래 상태 복원 실패:', error);
  }
  next();
});

const handleDeviceChecked = (data) => {
  const liveDevice = allSelectableLights.value.find((d) => String(d._id) === String(data.device._id)) || data.device;
  const id = String(liveDevice._id);
  if (data.isChecked) {
    if (!selectedLights.value.some((d) => String(d._id) === id)) {
      selectedLights.value = [...selectedLights.value, { ...liveDevice, showState: false }];
    }
  } else {
    selectedLights.value = selectedLights.value.filter((d) => String(d._id) !== id);
  }
};

const handleShowStateChanged = (data) => {
  const id = String(data?.device?._id);
  if (!id) return;
  selectedLights.value = selectedLights.value.map((d) => (
    String(d._id) === id ? { ...d, showState: data.showState === true } : d
  ));
};

const onSaveClick = () => {
  if (deviceName.value.length === 0) {
    emitter.emit('showPopAlert', { description: '장치 이름을 선택해주세요.' });
    return;
  }
  // 수정: 0개 = 매핑 해제 허용 / 등록: 1개 이상 필요
  if (!isEditMode.value && selectedLights.value.length === 0) {
    emitter.emit('showPopAlert', { description: '포함할 조명을 1개 이상 선택해주세요.' });
    return;
  }
  if (isEditMode.value) {
    requestUpdateMapping();
  } else {
    requestCreateButton();
  }
};

const resolveSelectedItems = () => {
  return selectedLights.value.map((sel) => {
    const current = allSelectableLights.value.find((d) => String(d._id) === String(sel._id));
    const base = current ? { ...current } : { ...sel };
    return { ...base, showState: sel.showState === true };
  });
};

const pushButtonMapsToBoard = async (serial) => {
  if (!serial) return;
  try {
    await store.dispatch('home/fetchAllZones');
    const maps = await buildButtonMapsForController(serial, store.getters['home/getZones']);
    publishButtonMapsPayload(mqttControl, serial, maps);
  } catch (e) {
    console.error('button maps push failed:', e);
  }
};

const requestCreateButton = async () => {
  if (portQuery.value == null || !serialQuery.value) {
    emitter.emit('showPopAlert', { description: '연결 포트 정보가 없습니다. 목록에서 다시 선택해주세요.' });
    return;
  }
  const payload = {
    controllerId: serialQuery.value,
    name: deviceName.value,
    type: 1,
    isVirtual: 0,
    item: { ports: [Number(portQuery.value)] },
    linkedItems: resolveSelectedItems(),
  };
  try {
    const result = await Axios.post(
      GroupApi.REGIST_GROUP_DEVICES.replace(':id', route.params.groupId),
      payload,
    );
    if (result.resultCode === '0000') {
      store.commit('home/removeSettingModeDevice', {
        serial: serialQuery.value,
        type: 1,
        data: { port: Number(portQuery.value) },
      });
      await pushButtonMapsToBoard(serialQuery.value);
      emitter.emit('showPopAlert', {
        description: '조명 버튼이 등록되었습니다.',
        okCallback: () => router.go(-1),
      });
      return;
    }
    emitter.emit('showPopAlert', { description: result.resultMessage });
  } catch (e) {
    emitter.emit('showPopAlert', { description: '조명 버튼 등록 중 오류가 발생하였습니다.' });
  }
};

const requestUpdateMapping = async () => {
  const payload = {
    items: resolveSelectedItems(),
    name: deviceName.value,
  };
  try {
    const result = await Axios.patch(
      VirtualDeviceApi.UPDATE_VIRTUAL_DEVICE.replace(':id', route.params.deviceId),
      payload,
    );
    if (result.resultCode === '0000') {
      const serial = buttonDevice.value?.controllerId;
      await pushButtonMapsToBoard(serial);
      const cleared = (payload.items || []).length === 0;
      emitter.emit('showPopAlert', {
        description: cleared
          ? '조명 버튼 매핑이 해제되었습니다.'
          : '조명 버튼 매핑이 수정되었습니다.',
        okCallback: () => router.go(-1),
      });
      return;
    }
    emitter.emit('showPopAlert', { description: result.resultMessage });
  } catch (e) {
    emitter.emit('showPopAlert', { description: '조명 버튼 수정 중 오류가 발생하였습니다.' });
  }
};
</script>

<style scoped>
.instruction-card,
.name-edit-card,
.section-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.instruction-header,
.section-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.section-header-toggle {
  cursor: pointer;
  user-select: none;
}

.section-header-collapsed {
  border-bottom: none;
}

.instruction-title,
.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.other-room-badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: #0d6efd;
  background-color: #e7f1ff;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  white-space: nowrap;
}

.instruction-body,
.section-body {
  padding: 0.75rem 1rem;
}

.name-edit-body {
  padding: 0.75rem 1rem;
}
</style>
